import type { DigestEntry } from "./types.js";

/** The structured dream-digest payload — one standing report per cycle, per brain. */
export interface DreamDigest {
  worker: string;
  /** The cursor-window upper bound (ISO); load-bearing for retention + rebase-retry idempotent re-write. */
  cycle: string;
  entries: DigestEntry[];
}

/**
 * Assemble the structured digest. The union is kept VERBATIM (append-only seam-4):
 * new entry kinds — like `promotion` — and channels append without re-architecture. No lossy
 * flattening here — the .json file IS this payload; render is a separate concern.
 */
export function assembleDigest(a: { worker: string; cycle: string; entries: DigestEntry[] }): DreamDigest {
  return { worker: a.worker, cycle: a.cycle, entries: [...a.entries] };
}

function fmtEvidence(evidence?: string[]): string {
  return evidence && evidence.length > 0 ? ` _(evidence: ${evidence.join(", ")})_` : "";
}

/**
 * Human-first markdown render. Grouped by known kind; the `default` arm render-degrades
 * an UNKNOWN kind generically (surfaces `kind` + `detail`/`evidence`) so the `promotion`
 * entry renders without a code change — the seam-4 render contract. Never throws.
 *
 * @param opts.title - Override the heading prefix (default: "Dream digest"). The librarian
 *   passes "Librarian digest" so its artifacts/librarian-digest/*.md files have the right heading.
 */
export function renderDigestMarkdown(digest: DreamDigest, opts?: { title?: string }): string {
  const title = opts?.title ?? "Dream digest";
  const lines: string[] = [];
  lines.push(`# ${title} — ${digest.worker}`);
  lines.push("");
  lines.push(`> Cycle (window upper bound): \`${digest.cycle}\``);
  lines.push("");

  const sectionLines: Record<string, string[]> = {};
  const push = (header: string, line: string) => {
    (sectionLines[header] ??= []).push(line);
  };

  for (const e of digest.entries) {
    switch (e.kind) {
      case "changed":
        push("## Changed", `- \`${e.path}\` — **${e.verb.toUpperCase()}**${fmtEvidence(e.evidence)}`);
        break;
      case "flagged":
        push("## Flagged (protected origin — not edited)", `- \`${e.path}\` — ${e.tension}${fmtEvidence(e.evidence)}`);
        break;
      case "quarantined":
        push("## Quarantined", `- \`${e.path}\` — ${e.reason}${fmtEvidence(e.evidence)}`);
        break;
      case "capture-gap":
        // Honest framing: a dreamer-judged PROXY, not the scribe-decision bar.
        push("## Capture gaps (dreamer-judged proxy, not outcome-grounded)", `- ${e.detail}${fmtEvidence(e.evidence)}`);
        break;
      case "skip-ledger":
        push("## Skipped (ledger reconcile)", `- \`${e.reason}\` ×${String(e.count)}${e.sampleIds.length > 0 ? ` (e.g. ${e.sampleIds.join(", ")})` : ""}`);
        break;
      case "cost":
        push("## Cost / latency", `- model \`${e.model}\`: ${String(e.inputTokens)} in / ${String(e.outputTokens)} out tokens, ${String(e.ms)} ms`);
        break;
      case "no-op":
        push("## No-op", `- ${e.detail}${fmtEvidence(e.evidence)}`);
        break;
      case "rejected":
        push("## Rejected", `- ${e.detail}${fmtEvidence(e.evidence)}`);
        break;
      case "failure":
        push("## Failures", `- ${e.detail}${fmtEvidence(e.evidence)}`);
        break;
      case "health": {
        const flags = e.fileFlags.length > 0
          ? ` — oversized files: ${e.fileFlags.map(f => `\`${f.path}\` (${String(f.tokens)} tokens)`).join(", ")}`
          : "";
        push("## Health", `- health: index ${String(e.indexTokens)} tokens | duplication ratio ${String(e.duplicationRatio)} | recall: ${String(e.recall.entries)} entries, median age ${String(e.recall.medianAgeDays)}d, stale summaries ${String(e.recall.staleSummaries)}, orphans ${String(e.recall.orphans)}${flags}`);
        break;
      }
      case "archived":
        push("## Archived", `- \`${e.path}\` → \`${e.to}\` (reason: ${e.reason})`);
        break;
      case "codified":
        push("## Codified", `- \`${e.slug}\`${e.pr ? ` — PR ${e.pr}` : ""}${e.rejected ? ` — rejected: ${e.rejected}` : ""}`);
        break;
      case "promotion": {
        // notify-after: render LOUD so a HELD skill is never silently dropped.
        const icon = e.outcome === "auto-merged" ? "✅" : e.outcome === "rejected" ? "❌" : "🛑";
        const scoreStr = typeof e.score === "number" ? `, score ${String(e.score)}` : "";
        const tags = e.defaulted ? ` [SAFETY explicit: ${e.defaulted.safety ? "no" : "yes"}, COLLISION explicit: ${e.defaulted.collision ? "no" : "yes"}]` : "";
        push(
          "## Promotion",
          `- ${icon} \`${e.slug}\` — **${e.outcome.toUpperCase()}** (${e.decision}/${e.judgeStatus}${scoreStr}, origin: ${e.origin})${tags}${e.pr ? ` — PR ${e.pr}` : ""} — ${e.reason}`,
        );
        break;
      }
      default: {
        // Render-degradation: an unknown kind (`promotion`, future channels) renders
        // generically from whatever generic fields it carries. NEVER throw on unknown kind.
        const unknown = e as { kind: string; detail?: string; path?: string; evidence?: string[] };
        const detail = unknown.detail ?? unknown.path ?? "";
        push("## Other", `- (${unknown.kind})${detail ? ` ${detail}` : ""}${fmtEvidence(unknown.evidence)}`);
        break;
      }
    }
  }

  // Stable, human-readable section order (unknown "## Other" last).
  const order = [
    "## Changed",
    "## Flagged (protected origin — not edited)",
    "## Quarantined",
    "## Capture gaps (dreamer-judged proxy, not outcome-grounded)",
    "## Skipped (ledger reconcile)",
    "## No-op",
    "## Rejected",
    "## Failures",
    "## Cost / latency",
    "## Health",
    "## Archived",
    "## Codified",
    "## Promotion",
    "## Other",
  ];
  for (const header of order) {
    const body = sectionLines[header];
    if (!Object.hasOwn(sectionLines, header) || body.length === 0) continue;
    lines.push(header, "", ...body, "");
  }

  if (digest.entries.length === 0) {
    lines.push("_No entries this cycle._", "");
  }

  return lines.join("\n");
}
